'use client';

import { useRouter } from 'next/navigation';

import { ShareButton } from '@ps/features/sharing/ShareButton';
import type { ShareTarget } from '@ps/features/sharing/shareTarget';
import type { BioContext } from '@ps/functions/bio/bioContext';
import { ArrowLeft, Share2 } from 'lucide-react';

interface Props {
  title: string;
  /** The page's bio context — makes the share link custom-domain aware. */
  bioContext: BioContext;
  /** What this surface shares (the series, or the course chapter). */
  shareTarget: ShareTarget;
  /** Semantic parent the STANDALONE page hard-navigates Back to (a chapter → its course). */
  backHref: string;
  /** Rendered inside the intercept overlay → Back pops the overlay (`router.back`). */
  intercepted?: boolean;
}

/**
 * The clip-grid detail top bar — the sole interactive island of the visitor
 * series / course-chapter detail surfaces (back + share). Everything else (shell,
 * clip grid) is server-rendered. Back pops the intercept overlay, or hard-
 * navigates to the semantic parent on a standalone page; share opens the app-wide
 * `ShareModal` with the canonical link for `shareTarget`.
 */
export function SeriesDetailHeader({
  title,
  bioContext,
  shareTarget,
  backHref,
  intercepted,
}: Props) {
  const router = useRouter();

  return (
    <header className="border-raise/10 flex w-full shrink-0 items-center justify-between border-b py-3 pl-3 pr-12">
      {/* Intercept overlay → button that pops it; standalone page → real <a href>
          that hard-navigates to the semantic parent (chapter → its course). */}
      {intercepted ? (
        <button
          type="button"
          onClick={() => router.back()}
          aria-label="Back"
          className="text-content-1 flex h-7 w-7 items-center justify-center"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden />
        </button>
      ) : (
        <a
          href={backHref}
          aria-label="Back"
          className="text-content-1 flex h-7 w-7 items-center justify-center"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden />
        </a>
      )}
      <h1 className="text-content-1 text-sub line-clamp-1 flex-1 px-2 text-center">
        {title}
      </h1>
      <ShareButton
        bioContext={bioContext}
        target={shareTarget}
        title={title}
        aria-label={`Share ${title}`}
        className="text-content-1 flex h-7 w-7 items-center justify-center"
      >
        <Share2 className="h-5 w-5" aria-hidden />
      </ShareButton>
    </header>
  );
}
